import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Search, Briefcase } from 'lucide-react';
import { SiX, SiLinkedin, SiGithub } from 'react-icons/si';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { label: '首页', path: ROUTE_PATHS.HOME },
    { label: '职位列表', path: ROUTE_PATHS.JOBS },
    { label: '企业服务', path: ROUTE_PATHS.FOR_EMPLOYERS },
    { label: '关于我们', path: ROUTE_PATHS.ABOUT },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2 font-bold text-xl">
              <Briefcase className="h-6 w-6 text-primary" />
              <span>职聘网</span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors hover:text-primary ${
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <Button variant="ghost" size="icon">
                <Search className="h-5 w-5" />
              </Button>
              <Button>发布职位</Button>
            </div>

            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors hover:text-primary ${
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="flex flex-col gap-2 pt-2 border-t border-border">
                <Button variant="outline" className="w-full">
                  <Search className="h-4 w-4 mr-2" />
                  搜索职位
                </Button>
                <Button className="w-full">发布职位</Button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border bg-muted/30">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2 font-bold text-lg">
                <Briefcase className="h-5 w-5 text-primary" />
                <span>职聘网</span>
              </div>
              <p className="text-sm text-muted-foreground">
                连接优秀人才与理想企业，打造高效专业的招聘平台
              </p>
              <div className="flex gap-3">
                <a
                  href="https://x.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  <SiX className="h-5 w-5" />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  <SiLinkedin className="h-5 w-5" />
                </a>
                <a
                  href="https://github.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  <SiGithub className="h-5 w-5" />
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">求职者</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to={ROUTE_PATHS.JOBS} className="hover:text-primary transition-colors">
                    浏览职位
                  </Link>
                </li>
                <li>
                  <a href="#" className="hover:text-primary transition-colors">
                    职业建议
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-primary transition-colors">
                    简历模板
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-primary transition-colors">
                    面试技巧
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">企业服务</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to={ROUTE_PATHS.FOR_EMPLOYERS} className="hover:text-primary transition-colors">
                    发布职位
                  </Link>
                </li>
                <li>
                  <a href="#" className="hover:text-primary transition-colors">
                    人才搜索
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-primary transition-colors">
                    定价方案
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-primary transition-colors">
                    招聘指南
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">关于我们</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to={ROUTE_PATHS.ABOUT} className="hover:text-primary transition-colors">
                    公司介绍
                  </Link>
                </li>
                <li>
                  <a href="#" className="hover:text-primary transition-colors">
                    联系我们
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-primary transition-colors">
                    隐私政策
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-primary transition-colors">
                    服务条款
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>© 2026 职聘网. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}